import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { ScreenHeader } from '../../components/ScreenHeader';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import {
  formatUnreadBadge,
  useNotificationCount,
} from '../../context/NotificationCountContext';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import type { NotificationListItem } from '../../types/notification';
import { formatAttendanceTimestamp } from '../../utils/dateTime';
import { isWebPlatform } from '../../utils/webLayout';

type Props = NativeStackScreenProps<RootStackParamList, 'NotificationList'>;

function NotificationRow({
  item,
  onPress,
  palette,
}: {
  item: NotificationListItem;
  onPress: () => void;
  palette: ReturnType<typeof useAppColors>;
}) {
  const preview =
    item.messagePreview?.trim() ||
    item.title?.trim() ||
    'Notification';
  const createdAt = formatAttendanceTimestamp(item.createdAt, null);

  return (
    <Pressable
      testID="notification-row"
      accessibilityRole="button"
      accessibilityLabel={`${item.title}, ${item.isRead ? 'Read' : 'Unread'}`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      <View style={styles.rowTop}>
        <Text
          style={[
            styles.rowTitle,
            {
              color: palette.textPrimary,
              fontWeight: item.isRead ? '600' : '700',
            },
          ]}
        >
          {item.title}
        </Text>
        {!item.isRead ? (
          <View style={[styles.unreadDot, { backgroundColor: palette.primary }]} />
        ) : null}
      </View>
      <Text style={[styles.rowPreview, { color: palette.textSecondary }]}>
        {preview}
      </Text>
      <Text style={[styles.rowMeta, { color: palette.textMuted }]}>
        {createdAt}
        {item.notificationTypeLabel ? ` · ${item.notificationTypeLabel}` : ''}
      </Text>
    </Pressable>
  );
}

export function NotificationListScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const { isCompact } = useResponsiveLayout();
  /** Web tablet+: the list body is inset by xl, so inset the header banner too. */
  const insetWebHeader = isWebPlatform() && !isCompact;
  const notificationCount = useNotificationCount();
  const notifications = useNotifications(auth.permissions);
  const capabilities = notifications.capabilities;
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const loadNotifications = notifications.loadNotifications;
  useFocusEffect(
    useCallback(() => {
      loadNotifications().catch(() => undefined);
    }, [loadNotifications]),
  );

  const unreadLabel =
    notifications.countUnavailable || notifications.unreadCount === null
      ? 'Unread count unavailable'
      : `${notifications.unreadCount} unread`;

  const badge = formatUnreadBadge(notifications.unreadCount);
  const showList =
    capabilities.canViewList && !notifications.historyUnavailable;

  const themed = useMemo(
    () => ({
      sectionTitle: { ...typography.sectionTitle, color: palette.textPrimary },
      metric: { ...typography.screenTitle, color: palette.primary },
      meta: {
        ...typography.caption,
        color: palette.textSecondary,
        marginBottom: spacing.lg,
      },
      body: { ...typography.body, color: palette.textSecondary },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.md,
      },
      info: {
        ...typography.caption,
        color: palette.info,
        marginBottom: spacing.sm,
      },
    }),
    [palette],
  );

  const confirmMarkAll = () => {
    if (
      !capabilities.canMarkAllRead ||
      notifications.unreadCount === null ||
      notifications.unreadCount <= 0
    ) {
      return;
    }

    Alert.alert(
      'Mark all as read',
      'Mark all notifications as read?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Mark all as read',
          onPress: () => {
            notifications.markAllRead().then(ok => {
              if (ok) {
                setActionMessage('All notifications marked as read.');
              }
            });
          },
        },
      ],
    );
  };

  const openDetails = useCallback(
    (item: NotificationListItem) => {
      navigation.navigate(routes.notificationDetails, {
        notificationId: item.notificationId,
      });
    },
    [navigation],
  );

  if (notifications.isLoading && notifications.unreadCount === null) {
    return (
      <AppScreen contentStyle={styles.center}>
        <ScreenHeader
          title="Notifications"
          onBack={navigation.goBack}
        />
        <AppLoader message="Loading notifications" />
      </AppScreen>
    );
  }

  if (showList) {
    return (
      <AppScreen contentStyle={styles.listScreen}>
        <ScreenHeader
          title="Notifications"
          onBack={navigation.goBack}
          bleed={!insetWebHeader}
        />

        <View style={styles.listBody}>
          {notifications.error && notifications.items.length === 0 ? (
            <ErrorState
              message={notifications.error}
              onRetry={notifications.retry}
            />
          ) : (
            <FlatList
              data={notifications.items}
              keyExtractor={item => String(item.notificationId)}
              refreshControl={
                <RefreshControl
                  refreshing={notifications.isRefreshing}
                  onRefresh={notifications.refreshNotifications}
                  tintColor={palette.primary}
                  accessibilityLabel="Refresh notifications"
                />
              }
              ListEmptyComponent={
                notifications.isLoading ? null : (
                  <EmptyState
                    title="No notifications"
                    message="No notifications were found."
                    actionTitle="Retry"
                    onAction={notifications.retry}
                  />
                )
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <NotificationRow
                  item={item}
                  onPress={() => openDetails(item)}
                  palette={palette}
                />
              )}
              onEndReached={() => {
                notifications.loadMore().catch(() => undefined);
              }}
              onEndReachedThreshold={0.4}
              ListFooterComponent={
                notifications.isLoadingMore ? (
                  <Text style={themed.meta}>Loading more…</Text>
                ) : null
              }
            />
          )}

          {notifications.statusMessage ? (
            <Text accessibilityRole="alert" style={themed.info}>
              {notifications.statusMessage}
            </Text>
          ) : null}
          {actionMessage ? (
            <Text accessibilityRole="alert" style={themed.info}>
              {actionMessage}
            </Text>
          ) : null}

          <View style={styles.actions}>
            {capabilities.canMarkAllRead &&
            notifications.unreadCount != null &&
            notifications.unreadCount > 0 ? (
              <AppButton
                title="Mark All as Read"
                onPress={confirmMarkAll}
                loading={notifications.isMarkingAllRead}
                accessibilityLabel="Mark all notifications as read"
              />
            ) : null}

            {capabilities.canViewPreferences ? (
              <AppButton
                title="Notification Preferences"
                variant="secondary"
                onPress={() =>
                  navigation.navigate(routes.notificationPreferences)
                }
              />
            ) : null}

          </View>
        </View>
      </AppScreen>
    );
  }

  return (
    <AppScreen
      scroll
      refreshControl={
        <RefreshControl
          refreshing={notifications.isRefreshing}
          onRefresh={notifications.refreshNotifications}
          tintColor={palette.primary}
          accessibilityLabel="Refresh notifications"
        />
      }
    >
      <ScreenHeader
        title="Notifications"
        onBack={navigation.goBack}
      />

      <View style={styles.body}>
        <Text style={themed.meta} accessibilityLabel={unreadLabel}>
          {unreadLabel}
          {badge ? ` (${badge})` : ''}
        </Text>

        {notifications.error ? (
          <ErrorState
            message={notifications.error}
            onRetry={notifications.retry}
          />
        ) : null}

        {notifications.countUnavailable ? (
          <Text accessibilityRole="alert" style={themed.warning}>
            Unread count is temporarily unavailable.
          </Text>
        ) : (
          <AppCard style={styles.card}>
            <Text style={themed.metric}>
              {notifications.unreadCount ?? 'Not available'}
            </Text>
            <Text style={themed.meta}>Unread notifications</Text>
          </AppCard>
        )}

        {notifications.historyUnavailable || !capabilities.canViewList ? (
          <AppCard style={styles.card}>
            <Text style={themed.sectionTitle}>Inbox unavailable</Text>
            <Text style={themed.body}>
              Notification history cannot be listed right now. Use Mark All as
              Read or Preferences below.
            </Text>
          </AppCard>
        ) : null}

        {notifications.statusMessage ? (
          <Text accessibilityRole="alert" style={themed.info}>
            {notifications.statusMessage}
          </Text>
        ) : null}
        {actionMessage ? (
          <Text accessibilityRole="alert" style={themed.info}>
            {actionMessage}
          </Text>
        ) : null}

        <View style={styles.actions}>
          {capabilities.canMarkAllRead &&
          notifications.unreadCount != null &&
          notifications.unreadCount > 0 ? (
            <AppButton
              title="Mark All as Read"
              onPress={confirmMarkAll}
              loading={notifications.isMarkingAllRead}
              accessibilityLabel="Mark all notifications as read"
            />
          ) : null}

          {capabilities.canViewPreferences ? (
            <AppButton
              title="Notification Preferences"
              variant="secondary"
              onPress={() => navigation.navigate(routes.notificationPreferences)}
            />
          ) : null}

        </View>

        {notificationCount.isLoadingCount && !notifications.isRefreshing ? (
          <Pressable accessibilityRole="text" accessibilityLabel="Updating unread count">
            <Text style={themed.meta}>Updating unread count…</Text>
          </Pressable>
        ) : null}
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  center: { justifyContent: 'center' },
  body: { marginTop: spacing.lg },
  listScreen: {
    flex: 1,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  listBody: { flex: 1 },
  listContent: { flexGrow: 1, paddingBottom: spacing.md },
  card: { gap: spacing.sm, marginBottom: spacing.md },
  actions: { gap: spacing.sm, marginTop: spacing.md, marginBottom: spacing.xl, paddingHorizontal: spacing.xl },
  row: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: spacing.xs,
  },
  rowTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  rowTitle: { ...typography.body, flex: 1 },
  rowPreview: { ...typography.caption },
  rowMeta: { ...typography.caption },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});
